import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { st, classes } from './CounterBadge.st.css';
import Caption from '../Text/Caption';
import Text from '../Text';
import { dataHooks } from './constants';
import { WixStyleReactContext } from '../WixStyleReactProvider/context';
import { WixStyleReactEnvironmentContext } from '../WixStyleReactEnvironmentProvider/context';
import { SupportedWixLocales } from 'wix-design-systems-locale-utils';
import { CounterBadgeProps, CounterBadgeSize } from './CounterBadge.types';

const MAX_NUMBER = 100;

type DefaultProps = Required<
  Pick<CounterBadgeProps, 'skin' | 'size' | 'truncate' | 'showShadow'>
>;
type Props = CounterBadgeProps & DefaultProps;

/** CounterBadge */
class CounterBadge extends PureComponent<Props> {
  static displayName = 'CounterBadge';
  static contextType = WixStyleReactEnvironmentContext;

  static propTypes = {
    dataHook: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
    locale: PropTypes.oneOf(SupportedWixLocales),
    skin: PropTypes.oneOf([
      'general',
      'standard',
      'neutralStandard',
      'danger',
      'warning',
      'urgent',
      'success',
      'light',
    ]),
    size: PropTypes.oneOf(['small', 'medium']),
    showShadow: PropTypes.bool,
    truncate: PropTypes.bool,
  };

  static defaultProps: DefaultProps = {
    skin: 'general',
    size: 'small',
    showShadow: false,
    truncate: true,
  };

  _renderNumberContent = (n: number, truncate: boolean) =>
    n < MAX_NUMBER || !truncate ? (
      Intl.NumberFormat(this._getLocale()).format(n)
    ) : (
      <div className={classes.numberContent}>
        {MAX_NUMBER - 1}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="5"
          height="5"
          viewBox="0 0 5 5"
        >
          <path
            fill="currentColor"
            d="M3,0 L3,2 L5,2 L5,3 L3,3 L3,5 L2,5 L2,3 L0,3 L0,2 L2,2 L2,0 L3,0 Z"
          />
        </svg>
      </div>
    );

  _renderCounterBadgeContent = (
    size: CounterBadgeSize,
    content: React.ReactNode,
  ) =>
    size === 'small' ? (
      <Caption
        tagName="span"
        caption="c1"
        light
        dataHook={dataHooks.content}
        className={classes.caption}
      >
        {content}
      </Caption>
    ) : (
      <Text
        dataHook={dataHooks.content}
        size="tiny"
        weight="bold"
        light
        className={classes.text}
      >
        {content}
      </Text>
    );

  _getLocale() {
    // @ts-ignore
    return this.props.locale || this.context.locale || 'en';
  }

  render() {
    const { dataHook, size, skin, children, className, showShadow, truncate } =
      this.props;
    const custom = isNaN(Number(children));
    const longNumber = !custom && truncate && Number(children) >= MAX_NUMBER;

    const content = custom
      ? children
      : this._renderNumberContent(Number(children), truncate);

    return (
      <WixStyleReactContext.Consumer>
        {({ newColorsBranding }) => (
          <div
            className={st(
              classes.root,
              { skin, custom, size, longNumber, showShadow, newColorsBranding },
              className,
            )}
            data-hook={dataHook}
            data-locale={this._getLocale()}
          >
            {this._renderCounterBadgeContent(size, content)}
          </div>
        )}
      </WixStyleReactContext.Consumer>
    );
  }
}

export default CounterBadge;
